import React, { useState, useEffect } from 'react';
import { differenceInSeconds } from 'date-fns';
import { Timer } from 'lucide-react';

const Countdown: React.FC = () => {
  const [targetDate, setTargetDate] = useState<string>(
    new Date(new Date().getFullYear() + 1, 0, 1).toISOString().slice(0, 16)
  );
  const [timeLeft, setTimeLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [isPassed, setIsPassed] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      const target = new Date(targetDate);
      const now = new Date();
      const diff = differenceInSeconds(target, now);

      if (diff <= 0) {
        setIsPassed(true);
        setTimeLeft({ d: 0, h: 0, m: 0, s: 0 });
      } else {
        setIsPassed(false);
        const d = Math.floor(diff / (3600 * 24));
        const h = Math.floor((diff % (3600 * 24)) / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = Math.floor(diff % 60);
        setTimeLeft({ d, h, m, s });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[500px]">
      <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 w-full max-w-4xl">
        <div className="flex flex-col items-center mb-10">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Timer className="text-sky-500" /> Countdown Timer
          </h2>
          <div className="flex items-center gap-4">
            <label className="font-medium text-slate-600">Target Date:</label>
            <input 
              type="datetime-local" 
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="px-4 py-2 bg-white border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>

        {isPassed ? (
          <div className="text-center py-10">
            <h3 className="text-4xl font-bold text-red-500">Time's Up!</h3>
            <p className="text-slate-500 mt-2">The selected date has passed.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <TimeBox value={timeLeft.d} label="Days" />
            <TimeBox value={timeLeft.h} label="Hours" />
            <TimeBox value={timeLeft.m} label="Minutes" />
            <TimeBox value={timeLeft.s} label="Seconds" />
          </div>
        )}
      </div>
    </div>
  );
};

const TimeBox: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="flex flex-col items-center p-6 bg-slate-900 rounded-xl text-white shadow-lg transform hover:scale-105 transition-transform">
    <span className="text-5xl md:text-7xl font-mono font-bold text-sky-400">
      {value.toString().padStart(2, '0')}
    </span>
    <span className="text-sm uppercase tracking-widest mt-2 text-slate-400 font-semibold">{label}</span>
  </div>
);

export default Countdown;